import { Box, Radio, TableCell, TableRow, Typography, useTheme } from "@mui/material"
import CustomTable from "../../investorOverview/common/customTable"
import { PaginationObj, TableHeaderData } from "../../investorOverview/types"
import { FamilyForRiaFeeSetupObj } from "../../../redux-store/types/onBoarding"

interface FamilyTableRowProps {
    row: FamilyForRiaFeeSetupObj;
    isPinned: boolean;
    isSelected: boolean;
    onToggle: (id: string) => void;
}

function FamilyTableRow({
    row,
    isPinned,
    isSelected,
    onToggle,
}: FamilyTableRowProps): JSX.Element {
    const theme = useTheme()

    return (
        <TableRow
            sx={{
                '& td': {
                    paddingTop: "10px",
                    paddingBottom: "10px",
                    borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                },
                '& td:first-of-type': {
                    paddingLeft: "15px",
                },
                ...(isPinned && {
                    backgroundColor: `${theme.palette.primary[500]}14`,
                    '& td': {
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        borderTop: `1px solid ${theme.palette.primary[500]}`,
                        borderBottom: `1px solid ${theme.palette.primary[500]}`,
                    },
                    '& td:first-of-type': {
                        paddingLeft: "15px",
                        borderLeft: `3px solid ${theme.palette.primary[500]}`,
                    },
                    '& td:last-of-type': {
                        borderRight: `1px solid ${theme.palette.primary[500]}`,
                    },
                }),
            }}
        >
            <TableCell align="center" padding="checkbox">
                <Radio
                    size="small"
                    checked={isSelected}
                    value={row.family_id}
                    onClick={() => onToggle(row.family_id)}
                    sx={{
                        color: theme.palette.text.secondary,
                        '&.Mui-checked': {
                            color: theme.palette.primary[500],
                        },
                    }}
                    inputProps={{ 'aria-label': `select-${row.family_name}` }}
                />
            </TableCell>
            <TableCell align="left">
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        flexWrap: "wrap",
                    }}
                >
                    <Typography
                        variant="body_500"
                        onClick={() => onToggle(row.family_id)}
                        sx={{
                            lineHeight: "20px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.primary,
                            cursor: "pointer"
                        }}
                    >
                        {row?.family_name ?? "-"}
                    </Typography>
                    {isPinned && (
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "2px 8px",
                                borderRadius: "10px",
                                backgroundColor: theme.palette.primary[500],
                            }}
                            aria-label="Selected row indicator"
                        >
                            <Typography
                                variant="overline_400"
                                sx={{
                                    fontWeight: 500,
                                    lineHeight: "14px",
                                    letterSpacing: "0.04px",
                                    textTransform: "uppercase",
                                    color: theme.palette.common.white,
                                }}
                            >
                                Selected
                            </Typography>
                        </Box>
                    )}
                </Box>
            </TableCell>
            <TableCell align="center">
                <Typography
                    variant="body_500"
                    sx={{
                        lineHeight: "20px",
                        letterSpacing: "0.02px",
                        color: theme.palette.text.primary,
                    }}
                >
                    {row?.family_id ?? "-"}
                </Typography>
            </TableCell>
            <TableCell align="center">
                <Typography
                    variant="body_500"
                    sx={{
                        lineHeight: "20px",
                        letterSpacing: "0.02px",
                        color: theme.palette.text.primary,
                    }}
                >
                    {row?.hof_name ?? "-"}
                </Typography>
            </TableCell>
            {/* <TableCell align="center">
                <Typography
                    variant="body_500"
                    sx={{
                        lineHeight: "20px",
                        letterSpacing: "0.02px",
                        color: theme.palette.text.primary,
                    }}
                >
                    {row?.rule_type ?? "-"}
                </Typography>
            </TableCell> */}
            <TableCell align="center">
                <Typography
                    variant="body_500"
                    sx={{
                        lineHeight: "20px",
                        letterSpacing: "0.02px",
                        color: theme.palette.text.primary,
                    }}
                >
                    {row?.hof_code ?? "-"}
                </Typography>
            </TableCell>
        </TableRow>
    )
}

interface DataTableProps {
    tableData: Array<FamilyForRiaFeeSetupObj>;
    selectedFamilyId: string;
    onRadioToggle: (id: string) => void;
}

function DataTable({
    tableData,
    selectedFamilyId,
    onRadioToggle,
}: DataTableProps): JSX.Element {
    const bodyRows = selectedFamilyId
        ? tableData.filter((row: FamilyForRiaFeeSetupObj) => row.family_id !== selectedFamilyId)
        : tableData

    return (
        <>
            {bodyRows.map((row: FamilyForRiaFeeSetupObj) => (
                <FamilyTableRow
                    key={row.family_id}
                    row={row}
                    isPinned={false}
                    isSelected={false}
                    onToggle={onRadioToggle}
                />
            ))}
        </>
    )
}

const headerData: TableHeaderData[] = [
    {
        id: 'radio',
        label: '',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'family_name',
        label: 'Family Name',
        align: 'left',
        isSortable: false,
    },
    {
        id: 'family_id',
        label: 'Family Code',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'hof_name',
        label: 'HOF Name',
        align: 'center',
        isSortable: false,
    },
    // {
    //     id: 'rule_type',
    //     label: 'Fee Type',
    //     align: 'center',
    //     isSortable: false,
    // },
    {
        id: 'hof_code',
        label: 'RNX Code',
        align: 'center',
        isSortable: false,
    },
]

interface FamilyListTableProps {
    isLoading: boolean;
    tableData: Array<FamilyForRiaFeeSetupObj>;
    tableState: PaginationObj & { debouncedSearchText: string };
    handleTableRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleTablePageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
    selectedRow: FamilyForRiaFeeSetupObj | null;
    onRadioToggle: (id: string) => void;
}

export default function FamilyListTable({
    isLoading,
    tableData,
    tableState,
    handleTableRowsPerPageChange,
    handleTablePageChange,
    selectedRow,
    onRadioToggle
}: FamilyListTableProps): JSX.Element {

    const selectedFamilyId = selectedRow?.family_id ?? ""

    return (
        <CustomTable
            isLoading={isLoading}
            tableBody={
                <DataTable
                    tableData={tableData}
                    selectedFamilyId={selectedFamilyId}
                    onRadioToggle={onRadioToggle}
                />
            }
            pinnedRow={selectedRow ? (
                <FamilyTableRow
                    row={selectedRow}
                    isPinned={true}
                    isSelected={true}
                    onToggle={onRadioToggle}
                />
            ) : null}
            tableData={tableData}
            tableHeaderData={headerData}
            tableHeadRowCustomStyles={{
                '& th:first-of-type': {
                    paddingLeft: "15px"
                }
            }}
            tableBodyCustomStyles={{
                '& tr:last-child td': {
                    border: 0
                }
            }}
            loadingRowsNum={7}
            tableDataCount={tableState.totalRows}
            rowsPerPage={tableState.rowsPerPage}
            rowsPerPageOptions={[5, 10, 15, 20]}
            handleRowsPerPageChange={handleTableRowsPerPageChange}
            handlePageChange={handleTablePageChange}
            currentPage={tableState.currentPage}
        />
    )
}